import React, { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { selectThemeMode } from '../../../redux/slices/themeSlice';

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100dvw;
  height: 100dvh;
  pointer-events: none;
`;

const Particles = ({
  x = 200,
  y = 200,
  isHalloween = false,
  isValentines = false,
}) => {
  const canvasRef = useRef(null);

  const theme = useTheme();
  const themeMode = useSelector(selectThemeMode);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Fullscreen canvas
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const config = {
      particleNumber: 400,
      maxParticleSize: 10,
      maxSpeed: 40,
      colorVariation: 1000,
    };

    const colorPalette = {
      bg: { r: 12, g: 9, b: 29 },
      matter: [
        { r: 36, g: 18, b: 42 },
        { r: 78, g: 36, b: 42 },
        { r: 252, g: 178, b: 96 },
        { r: 253, g: 238, b: 152 },
      ],
    };

    let particles = [];
    let isAnimating = true;

    const clearCanvas = (ctx) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const colorVariation = (color) => {
      const r = Math.round(
        Math.random() * config.colorVariation -
          config.colorVariation / 2 +
          color.r,
      );
      const g = Math.round(
        Math.random() * config.colorVariation -
          config.colorVariation / 2 +
          color.g,
      );
      const b = Math.round(
        Math.random() * config.colorVariation -
          config.colorVariation / 2 +
          color.b,
      );
      const a = Math.random() + 0.5;
      return `rgba(${r},${g},${b},${a})`;
    };

    function Particle(x, y) {
      this.x = x || Math.random() * canvas.width;
      this.y = y || Math.random() * canvas.height;
      this.r = Math.ceil(Math.random() * config.maxParticleSize);
      this.c = colorVariation(
        colorPalette.matter[
          Math.floor(Math.random() * colorPalette.matter.length)
        ],
      );
      this.s = Math.pow(Math.ceil(Math.random() * config.maxSpeed), 0.7);
      this.d = Math.round(Math.random() * 360);
    }

    const updateParticleModel = (p) => {
      const a = 180 - (p.d + 90);
      if (p.d > 0 && p.d < 180) {
        p.x += (p.s * Math.sin(p.d)) / Math.sin(p.s);
      } else {
        p.x -= (p.s * Math.sin(p.d)) / Math.sin(p.s);
      }
      if (p.d > 90 && p.d < 270) {
        p.y += (p.s * Math.sin(a)) / Math.sin(p.s);
      } else {
        p.y -= (p.s * Math.sin(a)) / Math.sin(p.s);
      }
      return p;
    };

    const drawBat = (x, y, size, c) => {
      ctx.save();
      ctx.translate(x, y);

      // Use orange for dark mode, black for light mode
      const batColor = themeMode === 'dark' ? '#FF8C00' : '#000000';
      ctx.fillStyle = batColor;

      // Scale factor: SVG viewBox is 24x24, scale to particle size
      const svgScale = size / 12;

      ctx.beginPath();

      // Convert SVG path to canvas commands
      // Original SVG path: M0.75 8C0.75 8 5 7 8 9C8 9 8.5 12.75 10.5 12.75V11C10.5 11 11 12 12 12C13 12 13.5 11 13.5 11V12.75C15.5 12.75 16 9 16 9C19 7 23.25 8 23.25 8C21.25 9 21 12.5 21 12.5C17 12.5 17 15.75 17 15.75C12 14.75 12 18.5 12 18.5C12 18.5 12 14.75 7 15.75C7 15.75 7 12.5 3 12.5C3 12.5 2.75 9 0.75 8Z
      // Scale and center: SVG center is 12,12, so offset by -12

      // M0.75 8 -> move to (0.75-12, 8-12) = (-11.25, -4) then scale
      ctx.moveTo((0.75 - 12) * svgScale, (8 - 12) * svgScale);

      // C0.75 8 5 7 8 9
      ctx.bezierCurveTo(
        (0.75 - 12) * svgScale,
        (8 - 12) * svgScale,
        (5 - 12) * svgScale,
        (7 - 12) * svgScale,
        (8 - 12) * svgScale,
        (9 - 12) * svgScale,
      );

      // C8 9 8.5 12.75 10.5 12.75
      ctx.bezierCurveTo(
        (8 - 12) * svgScale,
        (9 - 12) * svgScale,
        (8.5 - 12) * svgScale,
        (12.75 - 12) * svgScale,
        (10.5 - 12) * svgScale,
        (12.75 - 12) * svgScale,
      );

      // V11 -> vertical line (current x is 10.5-12)
      ctx.lineTo((10.5 - 12) * svgScale, (11 - 12) * svgScale);

      // C10.5 11 11 12 12 12
      ctx.bezierCurveTo(
        (10.5 - 12) * svgScale,
        (11 - 12) * svgScale,
        (11 - 12) * svgScale,
        (12 - 12) * svgScale,
        (12 - 12) * svgScale,
        (12 - 12) * svgScale,
      );

      // C13 12 13.5 11 13.5 11
      ctx.bezierCurveTo(
        (13 - 12) * svgScale,
        (12 - 12) * svgScale,
        (13.5 - 12) * svgScale,
        (11 - 12) * svgScale,
        (13.5 - 12) * svgScale,
        (11 - 12) * svgScale,
      );

      // V12.75
      ctx.lineTo((13.5 - 12) * svgScale, (12.75 - 12) * svgScale);

      // C15.5 12.75 16 9 16 9
      ctx.bezierCurveTo(
        (15.5 - 12) * svgScale,
        (12.75 - 12) * svgScale,
        (16 - 12) * svgScale,
        (9 - 12) * svgScale,
        (16 - 12) * svgScale,
        (9 - 12) * svgScale,
      );

      // C19 7 23.25 8 23.25 8
      ctx.bezierCurveTo(
        (19 - 12) * svgScale,
        (7 - 12) * svgScale,
        (23.25 - 12) * svgScale,
        (8 - 12) * svgScale,
        (23.25 - 12) * svgScale,
        (8 - 12) * svgScale,
      );

      // C21.25 9 21 12.5 21 12.5
      ctx.bezierCurveTo(
        (21.25 - 12) * svgScale,
        (9 - 12) * svgScale,
        (21 - 12) * svgScale,
        (12.5 - 12) * svgScale,
        (21 - 12) * svgScale,
        (12.5 - 12) * svgScale,
      );

      // C17 12.5 17 15.75 17 15.75
      ctx.bezierCurveTo(
        (17 - 12) * svgScale,
        (12.5 - 12) * svgScale,
        (17 - 12) * svgScale,
        (15.75 - 12) * svgScale,
        (17 - 12) * svgScale,
        (15.75 - 12) * svgScale,
      );

      // C12 14.75 12 18.5 12 18.5
      ctx.bezierCurveTo(
        (12 - 12) * svgScale,
        (14.75 - 12) * svgScale,
        (12 - 12) * svgScale,
        (18.5 - 12) * svgScale,
        (12 - 12) * svgScale,
        (18.5 - 12) * svgScale,
      );

      // C12 18.5 12 14.75 7 15.75
      ctx.bezierCurveTo(
        (12 - 12) * svgScale,
        (18.5 - 12) * svgScale,
        (12 - 12) * svgScale,
        (14.75 - 12) * svgScale,
        (7 - 12) * svgScale,
        (15.75 - 12) * svgScale,
      );

      // C7 15.75 7 12.5 3 12.5
      ctx.bezierCurveTo(
        (7 - 12) * svgScale,
        (15.75 - 12) * svgScale,
        (7 - 12) * svgScale,
        (12.5 - 12) * svgScale,
        (3 - 12) * svgScale,
        (12.5 - 12) * svgScale,
      );

      // C3 12.5 2.75 9 0.75 8
      ctx.bezierCurveTo(
        (3 - 12) * svgScale,
        (12.5 - 12) * svgScale,
        (2.75 - 12) * svgScale,
        (9 - 12) * svgScale,
        (0.75 - 12) * svgScale,
        (8 - 12) * svgScale,
      );

      // Z -> close path
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    };

    const drawHeart = (x, y, size, c) => {
      ctx.save();
      ctx.translate(x, y);

      const heartColor = themeMode === 'dark' ? '#FF4B6E' : '#E0245E';
      ctx.fillStyle = heartColor;

      // Simple symmetric heart shape using two bezier curves
      // Base coordinates are roughly in a [-1.5, 1.5] box, then scaled by size
      // Increase scale factor so hearts appear larger than default particles
      const baseScale = (size * 4) / 10;
      const scaleX = baseScale;
      // Slightly compress Y so hearts look more square than tall
      const scaleY = baseScale * 0.75;

      ctx.beginPath();
      ctx.moveTo(0 * scaleX, -1.5 * scaleY);
      ctx.bezierCurveTo(
        1.5 * scaleX,
        -3 * scaleY,
        3 * scaleX,
        -0.5 * scaleY,
        0 * scaleX,
        2.5 * scaleY,
      );
      ctx.bezierCurveTo(
        -3 * scaleX,
        -0.5 * scaleY,
        -1.5 * scaleX,
        -3 * scaleY,
        0 * scaleX,
        -1.5 * scaleY,
      );
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    };

    const drawParticle = (x, y, r, c) => {
      if (isValentines) {
        drawHeart(x, y, r, c);
      } else if (isHalloween) {
        drawBat(x, y, r, c);
      } else {
        ctx.beginPath();
        ctx.fillStyle = c;
        ctx.arc(x, y, r, 0, 2 * Math.PI, false);
        ctx.fill();
        ctx.closePath();
      }
    };

    const initParticles = (numParticles, x, y) => {
      for (let i = 0; i < numParticles; i++) {
        particles.push(new Particle(x, y));
      }
    };

    const frame = () => {
      if (isAnimating) {
        clearCanvas(ctx);
        particles = particles.map(updateParticleModel);
        particles.forEach((p) => drawParticle(p.x, p.y, p.r, p.c));
      }
      requestAnimationFrame(frame);
    };

    initParticles(config.particleNumber, x, y);
    frame();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isAnimating = false;
      window.removeEventListener('resize', handleResize);
    };
  }, [isHalloween, isValentines, x, y, themeMode]);

  return <Canvas ref={canvasRef} />;
};

export default Particles;
